import ClearIcon from "@mui/icons-material/Clear";
import SendIcon from "@mui/icons-material/Send";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";

import { TestIds } from "@storyboard/web-test-ids/testIds";
import AudioLevelIndicator from "@/components/Visualization/AudioLevelIndicator";
import type { VoiceRecorder } from "@/hooks/useVoiceRecorder";
import { zinc } from "@/ui/tokens";

/**
 * What covers the Dispatch composer while a voice message is on the go: the level meter, the timer, Cancel and Send
 * while recording, then the processing and the upload's progress once it is sent. The classic composer's overlay in
 * the page's compact frame; its ring takes the recorder's color as the recording nears its limit.
 */
export default function DispatchRecordingOverlay({ recorder }: { recorder: VoiceRecorder }) {
  return (
    <Box
      data-sb={TestIds.dispatch.composerRecording}
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 1,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        px: 1.25,
        borderRadius: "inherit",
        bgcolor: "rgba(39, 39, 42, 0.92)",
        boxShadow: `0 0 0 2px ${recorder.color || zinc[600]}`,
        color: zinc[100],
        fontSize: "0.8125rem",
      }}
    >
      {recorder.recording ? <Recording recorder={recorder} /> : <Sending uploadProgress={recorder.uploadProgress} />}
    </Box>
  );
}

function Recording({ recorder }: { recorder: VoiceRecorder }) {
  return (
    <>
      <AudioLevelIndicator audioLevel={recorder.audioLevel} color="var(--color-brand-primary-400)" size={22} />
      <Typography component="span" sx={{ fontSize: "0.875rem", fontVariantNumeric: "tabular-nums" }}>
        {recorder.time}
      </Typography>
      <Box sx={{ flex: 1 }} />
      <IconButton
        size="small"
        aria-label="Cancel recording"
        onClick={() => void recorder.cancel()}
        sx={{ color: zinc[200], bgcolor: zinc[700], "&:hover": { bgcolor: zinc[600] } }}
      >
        <ClearIcon sx={{ fontSize: 16 }} />
      </IconButton>
      <IconButton
        size="small"
        aria-label="Send recording"
        onClick={() => void recorder.stop()}
        sx={{ color: "white", bgcolor: "#16a34a", "&:hover": { bgcolor: "#22c55e" } }}
      >
        <SendIcon sx={{ fontSize: 16 }} />
      </IconButton>
    </>
  );
}

/** "Processing audio…" until the upload starts, then its progress as a percentage and a bar. */
function Sending({ uploadProgress }: { uploadProgress: number }) {
  if (uploadProgress === 0) return <Typography sx={{ fontSize: "inherit" }}>Processing audio…</Typography>;
  return (
    <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 1.25 }}>
      <Typography sx={{ fontSize: "inherit", flex: "none" }}>Uploading {uploadProgress}%</Typography>
      <Box sx={{ flex: 1, height: 4, borderRadius: 1, bgcolor: zinc[700] }}>
        <Box
          sx={{
            height: "100%",
            width: `${uploadProgress}%`,
            borderRadius: 1,
            background: "linear-gradient(90deg, #4ade80, #16a34a)",
            transition: "width 0.2s ease-out",
          }}
        />
      </Box>
    </Box>
  );
}
